export const parseJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

export const getPaymentPartnerId = (payload = {}) => {
  return (
    payload.partner_id ||
    payload.partnerId ||
    payload.team_id ||
    payload.teamId ||
    payload.metadata?.partner_id ||
    null
  );
};

const getCartItemPartnerId = (item = {}) =>
  item.partner_id || item.partnerId || item.team_id || item.teamId || null;

const limitMetadataValue = (value) => String(value).slice(0, 500);

export const resolveCartPartnerId = ({
  hostnamePartnerId = null,
  cartItems = [],
} = {}) => {
  const savedPartnerIds = [
    ...new Set((cartItems || []).map(getCartItemPartnerId).filter(Boolean)),
  ];

  if (savedPartnerIds.length > 1) {
    throw new Error(
      "Cart contains courses from multiple partner stores. Please check out one partner store at a time.",
    );
  }

  const savedPartnerId = savedPartnerIds[0] || null;
  if (
    hostnamePartnerId &&
    savedPartnerId &&
    hostnamePartnerId !== savedPartnerId
  ) {
    throw new Error(
      "Cart contains courses from a different partner store. Please check out from the original store or clear the cart.",
    );
  }

  return savedPartnerId || hostnamePartnerId || null;
};

export const buildCheckoutSourceMetadata = ({
  url,
  partnerId = null,
} = {}) => {
  const sourceUrl =
    url ||
    (typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
      : "");
  const metadata = {};

  if (sourceUrl) {
    const parsed = new URL(String(sourceUrl).split("#")[0]);
    metadata.checkout_source_hostname = limitMetadataValue(parsed.hostname);
    metadata.checkout_source_path = limitMetadataValue(
      `${parsed.pathname}${parsed.search}`,
    );
    metadata.checkout_source_url = limitMetadataValue(parsed.toString());
  }

  if (partnerId) {
    metadata.checkout_source_partner_id = limitMetadataValue(partnerId);
  }

  return metadata;
};

export const buildPaymentDiscountPayload = (payload = {}) => ({
  discountCodeId: payload.discountCodeId || payload.discount_code_id || null,
  discountCode: payload.discountCode || payload.discount_code || null,
  discountAmount: Number(
    payload.discountAmount || payload.discount_amount || 0,
  ),
  quantityDiscountAmount: Number(
    payload.quantityDiscountAmount || payload.quantity_discount_amount || 0,
  ),
  originalAmount:
    payload.originalAmount ??
    payload.original_amount ??
    payload.cartTotal ??
    null,
});

export const buildAccreditationTypes = (payload = {}) => {
  const fromObject = payload.accreditationTypes;
  if (
    fromObject &&
    typeof fromObject === "object" &&
    !Array.isArray(fromObject)
  ) {
    return fromObject;
  }

  const fromSerialized =
    payload.accreditation_types || payload.metadata?.accreditation_types;
  const parsed = parseJsonObject(fromSerialized);
  if (Object.keys(parsed).length > 0) return parsed;

  const courseId = payload.course_id || payload.courseId;
  const accreditationType =
    payload.accreditation_type || payload.accreditationType;
  if (courseId && accreditationType) {
    return { [courseId]: accreditationType };
  }

  return {};
};

export const buildRefresherInfo = (payload = {}) => {
  if (
    payload.refresherInfo &&
    typeof payload.refresherInfo === "object" &&
    !Array.isArray(payload.refresherInfo)
  ) {
    return payload.refresherInfo;
  }
  if (
    payload.parentCourseIds &&
    typeof payload.parentCourseIds === "object" &&
    !Array.isArray(payload.parentCourseIds)
  ) {
    return payload.parentCourseIds;
  }
  return parseJsonObject(
    payload.refresher_info || payload.metadata?.refresher_info,
  );
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const buildPaymentMetadata = (payload = {}) => {
  const metadata = { ...(payload.metadata || {}) };
  const courseIds = toArray(payload.courseIds || payload.course_ids);
  const bundleIds = toArray(payload.bundleIds || payload.bundle_ids);
  const partnerId = getPaymentPartnerId(payload);
  const accreditationTypes = buildAccreditationTypes(payload);
  const refresherInfo = buildRefresherInfo(payload);
  const sourceMetadata = buildCheckoutSourceMetadata({
    url: payload.sourceUrl || payload.source_url || payload.checkoutSourceUrl,
    partnerId,
  });

  if (courseIds.length > 0 || bundleIds.length > 0) {
    metadata.type = metadata.type || "cart_purchase";
    metadata.course_ids = JSON.stringify(courseIds);
    metadata.course_count = String(courseIds.length);
  }

  if (bundleIds.length > 0) {
    metadata.bundle_ids = JSON.stringify(bundleIds);
  }

  if (Object.keys(accreditationTypes).length > 0) {
    metadata.accreditation_types = JSON.stringify(accreditationTypes);
  }

  if (Object.keys(refresherInfo).length > 0) {
    metadata.refresher_info = JSON.stringify(refresherInfo);
  }

  if (partnerId) {
    metadata.partner_id = partnerId;
  }

  Object.assign(metadata, sourceMetadata);

  return metadata;
};
